'use client';

import { useFormContext } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UploadCloud, X, Loader2 } from 'lucide-react';
import { useState, useEffect, ChangeEvent } from 'react';
import { QuoteFormSchema } from './schema';
import { resizeImages } from '@/lib/resize-image';

export default function Step1System() {
	const {
		register,
		formState: { errors },
		setValue,
		setError,
		watch,
		getValues,
	} = useFormContext<QuoteFormSchema>();

	const isolationLevel = watch('system_isolation_level');
	const [previewUrls, setPreviewUrls] = useState<string[]>([]);
	const [isResizing, setIsResizing] = useState(false);

	// Watch for changes to the file list to update previews
	const locationPhotos = watch('location_photo');

	useEffect(() => {
		const files = (getValues('location_photo') as File[]) || [];
		if (files.length === 0) {
			setPreviewUrls([]);

			return;
		}

		const urls = files.map((file) => URL.createObjectURL(file));
		setPreviewUrls(urls);

		// Cleanup function to revoke URLs when component unmounts or files change
		return () => {
			urls.forEach((url) => URL.revokeObjectURL(url));
		};
	}, [locationPhotos, getValues]);

	const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		// Clear input immediately so the same file can be re-selected
		e.target.value = '';

		if (files.length > 0) {
			setIsResizing(true);
			try {
				// Resize images off the main thread where possible
				const resized = await resizeImages(files);

				const currentFiles = (getValues('location_photo') as File[]) || [];
				const newFiles = [...currentFiles, ...resized].slice(0, 5); // Limit to 5

				setValue('location_photo', newFiles, { shouldValidate: true });
			} catch {
				setError('location_photo', {
					type: 'manual',
					message: 'Foto verwerking mislukt. Probeer het opnieuw.',
				});
			} finally {
				setIsResizing(false);
			}
		}
	};

	const removeFile = (index: number) => {
		const currentFiles = (getValues('location_photo') as File[]) || [];
		const newFiles = currentFiles.filter((_, i) => i !== index);

		setValue('location_photo', newFiles, { shouldValidate: true });
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-2xl font-bold font-heading text-blue-dark mb-4">Stap 1: Uw Situatie</h3>
				<p className="text-lg text-gray-dark mb-8">
					Vertel ons iets meer over de ruimte die u wilt koelen of verwarmen.
				</p>
			</div>

			<div className="space-y-6">
				<div className="space-y-3">
					<Label htmlFor="system_surface_area" className="text-lg">
						Oppervlakte (m²) <span className="text-gray-400 font-normal">(optioneel)</span>
					</Label>
					<Input
						id="system_surface_area"
						type="number"
						placeholder="Bijv. 45"
						className={
							errors.system_surface_area ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg'
						}
						{...register('system_surface_area')}
					/>
					{errors.system_surface_area && <p className="text-sm text-red-500">{errors.system_surface_area.message}</p>}
				</div>

				<div className="space-y-3">
					<Label className="text-lg">
						Isolatieniveau <span className="text-gray-400 font-normal">(optioneel)</span>
					</Label>
					<Select
						onValueChange={(val: any) => setValue('system_isolation_level', val, { shouldValidate: true })}
						value={isolationLevel || ''} // Handle undefined
					>
						<SelectTrigger
							className={
								errors.system_isolation_level ? 'border-red-500 h-12 text-lg md:text-lg' : 'h-12 text-lg md:text-lg'
							}
						>
							<SelectValue placeholder="Selecteer isolatie niveau" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="slecht" className="text-lg py-3">
								Slecht (Enkel glas, geen spouwmuurisolatie)
							</SelectItem>
							<SelectItem value="matig" className="text-lg py-3">
								Matig (Dubbel glas, oudere woning)
							</SelectItem>
							<SelectItem value="goed" className="text-lg py-3">
								Goed (HR++ glas, na 2000)
							</SelectItem>
							<SelectItem value="zeer_goed" className="text-lg py-3">
								Zeer goed (Nieuwbouw, warmtepomp-ready)
							</SelectItem>
						</SelectContent>
					</Select>
					{errors.system_isolation_level && (
						<p className="text-sm text-red-500">{errors.system_isolation_level.message}</p>
					)}
				</div>

				<div className="space-y-3">
					<Label className="text-lg">
						Foto's van de situatie <span className="text-gray-400 font-normal">(optioneel, max 5)</span>
					</Label>
					<div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:bg-gray-50 transition-colors">
						{isResizing ? (
							<div className="flex flex-col items-center gap-2">
								<Loader2 className="size-10 text-blue-500 animate-spin" />
								<span className="text-lg font-medium text-blue-dark">Foto's verkleinen...</span>
							</div>
						) : (
							<label className="cursor-pointer block size-full">
								<UploadCloud className="mx-auto size-12 text-gray-400 mb-3" />
								<span className="text-lg font-medium text-blue-dark">Klik om te uploaden</span>
								<p className="text-sm text-gray-muted mt-2">Foto's worden automatisch verkleind</p>
								<input type="file" className="hidden" accept="image/*" multiple onChange={handleFileChange} />
							</label>
						)}
					</div>

					{previewUrls.length > 0 && (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
							{previewUrls.map((url, index) => (
								<div key={url} className="relative group">
									<img
										src={url}
										alt={`Preview ${index + 1}`}
										className="h-32 w-full object-cover rounded-md shadow-sm"
									/>
									<button
										type="button"
										onClick={() => removeFile(index)}
										className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md opacity-90 group-hover:opacity-100 hover:bg-red-600 focus:outline-none transition-opacity"
									>
										<X className="size-4" />
									</button>
								</div>
							))}
						</div>
					)}
					{errors.location_photo && (
						<p className="text-sm text-red-500 mt-2">{(errors.location_photo as any).message}</p>
					)}
				</div>
			</div>
		</div>
	);
}
